import React, { useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Card, CardBody, Table } from "reactstrap";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { findTableColumnDefinitionByPreferenceKey } from "helpers/GeneralHelper";
import { useTableColumnPreferences } from "libs/context/TableColumnPreferencesContext";
import ReportTableToolbar from "components/Common/ReportTableToolbar";

const getReviewStatusBadgeClass = (statusValue = "") => {
    const normalized = String(statusValue).trim().toLowerCase();
    if (normalized === "red") return "team-performance-review-badge--red";
    if (normalized === "yellow") return "team-performance-review-badge--yellow";
    if (normalized === "green") return "team-performance-review-badge--green";
    return "team-performance-review-badge--neutral";
};

const formatReviewStatusLabel = (value = "") => {
    const normalized = String(value).trim();
    if (!normalized) return "";
    return normalized.charAt(0).toUpperCase() + normalized.slice(1).toLowerCase();
};

const renderReviewStatusCell = (value) => {
    if (value === null || value === undefined || String(value).trim() === "") {
        return <span className="text-muted">—</span>;
    }

    const badgeClass = getReviewStatusBadgeClass(value);
    const label = formatReviewStatusLabel(value);

    return (
        <span
            className={`px-2 py-1 fw-semibold team-performance-review-badge ${badgeClass}`.trim()}
        >
            {label}
        </span>
    );
};

const resolveExportCellValue = (column, row = {}) => {
    const accessor = column?.accessor;
    const value = row?.[accessor];

    if (column?.isReviewStatus) {
        return formatReviewStatusLabel(value) || "";
    }

    if (column?.isStatusCount || column?.isStatusValue) {
        if (value === null || value === undefined || value === "") {
            return column?.isStatusCount ? "0" : "";
        }
        return String(value);
    }

    if (value === null || value === undefined) {
        return "";
    }

    return String(value);
};

const renderCellValue = (column, row, onStatusCountClick) => {
    const accessor = column?.accessor;
    const value = row?.[accessor];

    if (column?.isReviewStatus) {
        return renderReviewStatusCell(value);
    }

    if (column?.isStatusCount) {
        const displayValue =
            value === null || value === undefined || value === "" ? "0" : value;
        return (
            <button
                type="button"
                className="status-columns-report-value"
                onClick={() => onStatusCountClick?.(row, column)}
            >
                {displayValue}
            </button>
        );
    }

    if (column?.isStatusValue) {
        if (value === null || value === undefined || value === "") {
            return <span className="text-muted">—</span>;
        }
        return <span className="status-columns-report-value">{value}</span>;
    }

    if (value === null || value === undefined || value === "") {
        return <span className="text-muted">—</span>;
    }

    return <span>{value}</span>;
};

const SortIcon = ({ column, sortConfig }) => {
    const isActive = sortConfig.key === column;
    const direction = isActive ? sortConfig.direction : null;
    const activeColor = "#212529";
    const inactiveColor = "#c4c4c4";

    return (
        <span
            style={{
                display: "inline-flex",
                flexDirection: "column",
                lineHeight: 0,
                marginLeft: 4,
                verticalAlign: "middle",
            }}
        >
            <svg width="8" height="5" viewBox="0 0 8 5" style={{ display: "block" }}>
                <path d="M4 0l4 5H0z" fill={direction === "asc" ? activeColor : inactiveColor} />
            </svg>
            <svg width="8" height="5" viewBox="0 0 8 5" style={{ display: "block", marginTop: 2 }}>
                <path d="M4 5L0 0h8z" fill={direction === "desc" ? activeColor : inactiveColor} />
            </svg>
        </span>
    );
};

SortIcon.propTypes = {
    column: PropTypes.string,
    sortConfig: PropTypes.shape({
        key: PropTypes.string,
        direction: PropTypes.string,
    }),
};

const LeadsAddedReportTable = ({
    columns = [],
    data = [],
    onStatusCountClick,
    pageName = "LeadsAddedReport",
}) => {
    const resolvedTableKey = String(pageName || "LeadsAddedReport").trim();
    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
    const {
        columnOrder,
        visibleColumns,
        visibleColumnOrder,
        orderedColumns,
        handleColumnDragEnd,
        handleColumnToggle,
    } = useTableColumnPreferences(resolvedTableKey, columns);

    const visibleColumnDefinitions = useMemo(
        () =>
            visibleColumnOrder
                .map((preferenceKey) =>
                    findTableColumnDefinitionByPreferenceKey(columns, preferenceKey)
                )
                .filter(Boolean),
        [columns, visibleColumnOrder]
    );

    const handleSort = (accessor) => {
        if (!accessor) return;
        setSortConfig((prev) => {
            if (prev.key === accessor) {
                return {
                    key: accessor,
                    direction: prev.direction === "asc" ? "desc" : "asc",
                };
            }
            return { key: accessor, direction: "asc" };
        });
    };

    const sortedData = useMemo(() => {
        const rows = Array.isArray(data) ? data : [];
        if (!sortConfig.key) return rows;

        return [...rows].sort((a, b) => {
            const aValue = a[sortConfig.key];
            const bValue = b[sortConfig.key];

            if (aValue === null || aValue === undefined || aValue === "") return 1;
            if (bValue === null || bValue === undefined || bValue === "") return -1;

            if (typeof aValue === "number" && typeof bValue === "number") {
                return sortConfig.direction === "asc" ? aValue - bValue : bValue - aValue;
            }

            const aNumber = Number(aValue);
            const bNumber = Number(bValue);
            if (
                aValue !== "" &&
                bValue !== "" &&
                !Number.isNaN(aNumber) &&
                !Number.isNaN(bNumber) &&
                String(aValue).trim() !== "" &&
                String(bValue).trim() !== ""
            ) {
                return sortConfig.direction === "asc" ? aNumber - bNumber : bNumber - aNumber;
            }

            return sortConfig.direction === "asc"
                ? String(aValue).localeCompare(String(bValue))
                : String(bValue).localeCompare(String(aValue));
        });
    }, [data, sortConfig]);

    const getExportRows = useCallback(
        (exportColumns = []) =>
            sortedData.map((row) =>
                exportColumns.map((column) => resolveExportCellValue(column, row))
            ),
        [sortedData]
    );

    const getItemStyle = (isDragging, draggableStyle) => ({
        userSelect: "none",
        cursor: "grab",
        background: isDragging ? "#f1f5f9" : "transparent",
        ...draggableStyle,
    });

    return (
        <Card className="shadow-sm border-0">
            <CardBody className="p-0">
                <ReportTableToolbar
                    pageName={pageName}
                    tableKey={resolvedTableKey}
                    columns={columns}
                    visibleColumnOrder={visibleColumnOrder}
                    columnOrder={columnOrder}
                    orderedColumns={orderedColumns}
                    visibleColumns={visibleColumns}
                    onColumnToggle={handleColumnToggle}
                    getExportRows={getExportRows}
                    showCopy
                    showCsv
                />

                <div className="table-responsive">
                    <Table
                        hover
                        borderless
                        className="align-middle mb-0 custom-table leads-added-report-table"
                    >
                        <DragDropContext onDragEnd={handleColumnDragEnd}>
                            <Droppable droppableId="leads-added-report-columns" direction="horizontal">
                                {(provided) => (
                                    <thead
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                        style={{ backgroundColor: "#f8fafc" }}
                                    >
                                        <tr
                                            style={{
                                                color: "#374151",
                                                fontWeight: 600,
                                                fontSize: "14px",
                                            }}
                                        >
                                            {visibleColumnDefinitions.map((column, index) => (
                                                <Draggable
                                                    key={column.accessor}
                                                    draggableId={column.accessor}
                                                    index={index}
                                                >
                                                    {(dragProvided, snapshot) => (
                                                        <th
                                                            ref={dragProvided.innerRef}
                                                            {...dragProvided.draggableProps}
                                                            {...dragProvided.dragHandleProps}
                                                            className="text-nowrap"
                                                            onClick={() => handleSort(column?.accessor)}
                                                            style={getItemStyle(
                                                                snapshot.isDragging,
                                                                dragProvided.draggableProps.style
                                                            )}
                                                        >
                                                            {column?.Header}{" "}
                                                            <SortIcon
                                                                column={column?.accessor}
                                                                sortConfig={sortConfig}
                                                            />
                                                        </th>
                                                    )}
                                                </Draggable>
                                            ))}
                                            {provided.placeholder}
                                        </tr>
                                    </thead>
                                )}
                            </Droppable>
                        </DragDropContext>

                        <tbody>
                            {sortedData.length ? (
                                sortedData.map((row, rowIndex) => (
                                    <tr key={row?.clientId ?? row?.id ?? `leads-added-row-${rowIndex}`}>
                                        {visibleColumnDefinitions.map((column) => (
                                            <td key={`${rowIndex}-${column?.accessor}`}>
                                                {renderCellValue(column, row, onStatusCountClick)}
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td
                                        colSpan={Math.max(visibleColumnDefinitions.length, 1)}
                                        className="text-center text-muted py-4"
                                    >
                                        No records found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>
            </CardBody>
        </Card>
    );
};

LeadsAddedReportTable.propTypes = {
    columns: PropTypes.array,
    data: PropTypes.array,
    onStatusCountClick: PropTypes.func,
    pageName: PropTypes.string,
};

export default LeadsAddedReportTable;
